import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, SxProps, Theme } from '@mui/material';

export type LineSegment = Readonly<{ x1: number; y1: number; x2: number; y2: number }>;

type ConnectingLinesProps = Readonly<{
  containerRef: React.RefObject<HTMLDivElement | null>;
  calculate: (container: HTMLDivElement) => LineSegment[];
}>;

export function ConnectingLines({ containerRef, calculate }: ConnectingLinesProps) {
  const [segments, setSegments] = useState<LineSegment[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);

  const run = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    setSegments(calculate(container));
  }, [containerRef, calculate]);

  useEffect(() => {
    // Use rAF to ensure parent refs are attached before we read them.
    // Layout effects fire bottom-up, so a child's effect can run before
    // the parent's ref is assigned. Deferring one frame guarantees the
    // ref is available.
    const rafId = requestAnimationFrame(() => {
      run();

      const container = containerRef.current;

      if (!container) return;

      observerRef.current = new ResizeObserver(run);
      observerRef.current.observe(container);
    });

    return () => {
      cancelAnimationFrame(rafId);
      observerRef.current?.disconnect();
    };
  }, [run, containerRef]);

  if (segments.length === 0) return null;

  return (
    <Box component="svg" sx={svgSx}>
      {segments.map((seg, i) => (
        <line
          key={i}
          x1={seg.x1}
          y1={seg.y1}
          x2={seg.x2}
          y2={seg.y2}
          stroke="currentColor"
          strokeWidth={2}
        />
      ))}
    </Box>
  );
}

const svgSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  color: backrs.colors['Brand/500'],
});
